import { useState, useEffect } from "react";
import { format } from "date-fns";
import {
  CalendarDays,
  ChevronDown,
  ChevronRight,
  Download,
  FileText,
  Loader2,
  Plus,
  Trash2,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Users,
  Target,
  MessageSquare,
  Shield,
  AlertTriangle,
  Lightbulb,
  HandshakeIcon,
  ClipboardCheck,
  CheckSquare,
  ArrowRight,
  Crown,
  Wrench,
  Eye,
  HelpCircle,
  Info,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  useMeetingPreps,
  useMeetingPrepDetail,
  useCreateMeetingPrep,
  useUpdateMeetingPrep,
  useDeleteMeetingPrep,
  useDebriefMeetingPrep,
  generateMeetingPrepDocx,
  MEETING_TYPES,
  MEETING_PREP_STATUSES,
  MeetingPrep,
  MeetingPrepSummary,
  MeetingType,
  MeetingPrepStatus,
  AttendeeItem,
  ConfirmationQuestion,
  NewInfoQuestion,
  AttitudeQuestion,
  InfoToShare,
  CredibilityElement,
  ObjectionItem,
} from "@/hooks/useMeetingPrep";
import type { OpportunityContact } from "@/types/pipeline";
import { PIPELINE_STAGES } from "@/types/pipeline";
import { useUpdateOpportunityContact } from "@/hooks/useOpportunityDetail";

// ============= PROPS =============

interface MeetingPrepTabProps {
  opportunityId: string;
  accountId: string | null;
  accountName?: string | null;
  opportunityName?: string | null;
  opportunityContacts?: OpportunityContact[];
  stageName?: string | null;
  stageDescription?: string | null;
}

// ============= PRE-FLIGHT TYPES & CONSTANTS =============

interface PreFlightData {
  attendees: AttendeeItem[];
  primaryObjective: string;
  advancementGoal: string;
}

const BUYING_ROLE_CONFIG = [
  { value: "economic", label: "Economic Buyer", icon: Crown, color: "text-amber-600" },
  { value: "user", label: "User Buyer", icon: Eye, color: "text-blue-600" },
  { value: "technical", label: "Technical Buyer", icon: Wrench, color: "text-purple-600" },
  { value: "coach", label: "Coach", icon: MessageSquare, color: "text-green-600" },
  { value: "champion", label: "Champion", icon: Crown, color: "text-rose-600" },
  { value: "unknown", label: "Unknown", icon: HelpCircle, color: "text-gray-500" },
] as const;

const RELATIONSHIP_STRENGTH_OPTIONS = [
  { value: "cold", label: "Cold", color: "bg-blue-100 text-blue-700" },
  { value: "warm", label: "Warm", color: "bg-amber-100 text-amber-700" },
  { value: "strong", label: "Strong", color: "bg-green-100 text-green-700" },
  { value: "champion", label: "Champion", color: "bg-purple-100 text-purple-700" },
] as const;

const STAGE_SUGGESTED_GOALS: Record<string, { goals: string[]; nextStepHint: string }> = {
  "Strategic Foundation": {
    goals: [
      "Identify all buying influences and their roles",
      "Find and validate a Coach/Champion",
      "Confirm the Single Sales Objective with the prospect",
      "Map organizational decision-making process",
    ],
    nextStepHint: "Advance to Stakeholder Alignment: ensure every buying influence is identified and at least one Coach is engaged.",
  },
  "Stakeholder Alignment": {
    goals: [
      "Build individual win-results for each stakeholder",
      "Validate each buyer's concept of the solution",
      "Strengthen relationship with Economic Buyer",
      "Confirm budget authority and timeline expectations",
    ],
    nextStepHint: "Advance to Solution Development: all key stakeholders should have personalized win-results documented.",
  },
  "Solution Development": {
    goals: [
      "Present solution mapped to their specific criteria",
      "Get Technical Buyer sign-off on feasibility",
      "Confirm solution scoring against evaluation criteria",
      "Address any remaining concept gaps",
    ],
    nextStepHint: "Advance to Proposal & Navigation: solution should be fully validated with technical and user buyers.",
  },
  "Proposal & Navigation": {
    goals: [
      "Address Economic Buyer's remaining concerns",
      "Navigate internal politics and objections",
      "Finalize commercial terms and pricing",
      "Confirm decision timeline and process",
    ],
    nextStepHint: "Advance to Best Few / Close: all red flags should be resolved and terms agreed in principle.",
  },
  "Best Few / Close": {
    goals: [
      "Resolve final red flags and blockers",
      "Confirm contract terms and timeline",
      "Get verbal commitment or signature",
      "Align on onboarding/kickoff plan",
    ],
    nextStepHint: "Close the deal: ensure contract, budget release, and signed agreement.",
  },
  "Closed Won": {
    goals: [
      "Plan kickoff meeting agenda",
      "Confirm implementation timeline",
      "Introduce delivery team to key contacts",
    ],
    nextStepHint: "Focus on successful onboarding and relationship handoff.",
  },
};

// ============= STATUS BADGE =============

function StatusBadge({ status }: { status: MeetingPrepStatus }) {
  const config = MEETING_PREP_STATUSES.find((s) => s.value === status);
  if (!config) return null;
  return (
    <Badge
      variant="outline"
      className="text-xs"
      style={{
        backgroundColor: `${config.color}15`,
        color: config.color,
        borderColor: `${config.color}40`,
      }}
    >
      {config.label}
    </Badge>
  );
}

function MeetingTypeBadge({ type }: { type: MeetingType }) {
  const label = MEETING_TYPES.find((t) => t.value === type)?.label || type;
  return <Badge variant="secondary" className="text-xs">{label}</Badge>;
}

// ============= ACCORDION SECTION =============

function AccordionSection({
  title,
  icon: Icon,
  defaultOpen = false,
  children,
  badge,
}: {
  title: string;
  icon: React.ElementType;
  defaultOpen?: boolean;
  children: React.ReactNode;
  badge?: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center gap-2 w-full p-3 hover:bg-muted/50 rounded-lg transition-colors text-left">
        {open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
        <Icon className="h-4 w-4 shrink-0 text-primary" />
        <span className="text-sm font-medium flex-1">{title}</span>
        {badge}
      </CollapsibleTrigger>
      <CollapsibleContent className="px-3 pb-3 pt-1">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

// ============= DYNAMIC LIST HELPERS =============

function DynamicList<T extends Record<string, any>>({
  items,
  onUpdate,
  renderItem,
  newItem,
  addLabel = "Add",
}: {
  items: T[];
  onUpdate: (items: T[]) => void;
  renderItem: (item: T, index: number, update: (field: string, value: any) => void) => React.ReactNode;
  newItem: () => T;
  addLabel?: string;
}) {
  const addOne = () => onUpdate([...items, newItem()]);
  const removeAt = (i: number) => onUpdate(items.filter((_, idx) => idx !== i));
  const updateAt = (i: number, field: string, value: any) => {
    const next = [...items];
    next[i] = { ...next[i], [field]: value };
    onUpdate(next);
  };
  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex gap-2 items-start">
          <div className="flex-1 space-y-1">
            {renderItem(item, i, (field, value) => updateAt(i, field, value))}
          </div>
          <Button variant="ghost" size="sm" className="shrink-0 mt-1 h-7 w-7 p-0 text-muted-foreground hover:text-destructive" onClick={() => removeAt(i)}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addOne} className="text-xs">
        <Plus className="h-3 w-3 mr-1" />{addLabel}
      </Button>
    </div>
  );
}

// ============= DETAIL VIEW =============

function MeetingPrepDetail({
  prepId,
  accountName,
  opportunityName,
  onBack,
  onDeleted,
}: {
  prepId: string;
  accountName: string;
  opportunityName?: string | null;
  onBack: () => void;
  onDeleted: () => void;
}) {
  const { data: prep, isLoading } = useMeetingPrepDetail(prepId);
  const updatePrep = useUpdateMeetingPrep();
  const deletePrep = useDeleteMeetingPrep();
  const debriefPrep = useDebriefMeetingPrep();
  const [isDownloading, setIsDownloading] = useState(false);

  // Local form state — sync from server once loaded
  const [form, setForm] = useState<Partial<MeetingPrep>>({});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (prep) {
      setForm(prep);
      setDirty(false);
    }
  }, [prep]);

  const updateField = (field: string, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const handleSave = () => {
    if (!prep) return;
    const { id, created_at, created_by, ...updates } = form as MeetingPrep;
    updatePrep.mutate(
      { id: prep.id, ...updates },
      {
        onSuccess: () => { toast.success("Meeting prep saved"); setDirty(false); },
        onError: () => toast.error("Failed to save"),
      }
    );
  };

  const handleStatusChange = (status: MeetingPrepStatus) => {
    if (!prep) return;
    updatePrep.mutate(
      { id: prep.id, status },
      {
        onSuccess: () => toast.success(`Status → ${status}`),
        onError: () => toast.error("Failed to update status"),
      }
    );
  };

  const handleDelete = () => {
    if (!prep) return;
    if (!confirm("Delete this meeting prep?")) return;
    deletePrep.mutate(
      { id: prep.id, opportunityId: prep.opportunity_id },
      {
        onSuccess: () => { toast.success("Deleted"); onDeleted(); },
        onError: () => toast.error("Failed to delete"),
      }
    );
  };

  const handleDownload = async () => {
    if (!prep) return;
    setIsDownloading(true);
    try {
      await generateMeetingPrepDocx(prep, accountName, opportunityName);
      toast.success("DOCX downloaded");
    } catch (e) {
      console.error(e);
      toast.error("Failed to generate DOCX");
    } finally {
      setIsDownloading(false);
    }
  };

  // Debrief state
  const [showDebrief, setShowDebrief] = useState(false);
  const [debriefForm, setDebriefForm] = useState({
    objective_achieved: false,
    fallback_achieved: false,
    buying_influence_learnings: "",
    new_red_flags: "",
    new_strengths: "",
    next_action: "",
    next_action_owner: "",
    debrief_notes: "",
  });

  const handleDebrief = () => {
    if (!prep) return;
    debriefPrep.mutate(
      { id: prep.id, opportunityId: prep.opportunity_id, ...debriefForm },
      {
        onSuccess: () => { toast.success("Debrief saved & activity logged"); setShowDebrief(false); },
        onError: () => toast.error("Failed to save debrief"),
      }
    );
  };

  if (isLoading || !prep) {
    return (
      <div className="flex items-center justify-center h-[300px]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {/* Header bar */}
      <div className="flex items-center justify-between gap-2 pb-3 border-b">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onBack} className="h-8 w-8 p-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <MeetingTypeBadge type={form.meeting_type as MeetingType} />
              <StatusBadge status={form.status as MeetingPrepStatus} />
              <span className="text-xs text-muted-foreground">
                {format(new Date(form.meeting_date || ""), "MMM d, yyyy h:mm a")}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            disabled={isDownloading}
            className="text-xs"
          >
            {isDownloading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Download className="h-3.5 w-3.5 mr-1" />}
            DOCX
          </Button>
          {dirty && (
            <Button size="sm" onClick={handleSave} disabled={updatePrep.isPending} className="text-xs">
              {updatePrep.isPending ? "Saving..." : "Save"}
            </Button>
          )}
          <Select value={form.status as string} onValueChange={(v) => handleStatusChange(v as MeetingPrepStatus)}>
            <SelectTrigger className="h-8 w-[110px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MEETING_PREP_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive" onClick={handleDelete}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Meeting Setup (always visible, not accordion) */}
      <div className="space-y-3 py-3 border-b">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Meeting Date & Time</Label>
            <Input
              type="datetime-local"
              value={(form.meeting_date || "").slice(0, 16)}
              onChange={(e) => updateField("meeting_date", e.target.value ? new Date(e.target.value).toISOString() : "")}
              className="h-8 text-sm"
            />
          </div>
          <div>
            <Label className="text-xs">Meeting Type</Label>
            <Select value={form.meeting_type as string} onValueChange={(v) => updateField("meeting_type", v)}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MEETING_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs">Our Attendees (comma-separated)</Label>
          <Input
            value={(form.our_attendees || []).join(", ")}
            onChange={(e) => updateField("our_attendees", e.target.value.split(",").map((s: string) => s.trim()).filter(Boolean))}
            placeholder="e.g., Brian Elber, Nico Carr"
            className="h-8 text-sm"
          />
        </div>
        <div>
          <Label className="text-xs">Their Attendees</Label>
          <DynamicList<AttendeeItem>
            items={(form.their_attendees as AttendeeItem[]) || []}
            onUpdate={(items) => updateField("their_attendees", items)}
            newItem={() => ({ name: "", buying_role: "", key_concern: "" })}
            addLabel="Add attendee"
            renderItem={(item, _i, update) => (
              <div className="grid grid-cols-3 gap-2">
                <Input value={item.name} onChange={(e) => update("name", e.target.value)} placeholder="Name" className="h-7 text-xs" />
                <Select value={item.buying_role || ""} onValueChange={(v) => update("buying_role", v)}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Buying role" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="economic">Economic Buyer</SelectItem>
                    <SelectItem value="user">User Buyer</SelectItem>
                    <SelectItem value="technical">Technical Buyer</SelectItem>
                    <SelectItem value="coach">Coach/Champion</SelectItem>
                    <SelectItem value="unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
                <Input value={item.key_concern || ""} onChange={(e) => update("key_concern", e.target.value)} placeholder="Key concern" className="h-7 text-xs" />
              </div>
            )}
          />
        </div>
      </div>

      {/* Accordion Sections */}
      <div className="divide-y">
        {/* Section 1: Objectives */}
        <AccordionSection title="1. Objectives" icon={Target} defaultOpen={true}>
          <div className="space-y-2">
            <div>
              <Label className="text-xs">Primary Objective *</Label>
              <Textarea
                value={form.primary_objective || ""}
                onChange={(e) => updateField("primary_objective", e.target.value)}
                placeholder="What specific outcome must we achieve in this meeting?"
                rows={2}
                className="text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Minimum Acceptable Outcome</Label>
              <Input
                value={form.minimum_acceptable_outcome || ""}
                onChange={(e) => updateField("minimum_acceptable_outcome", e.target.value)}
                placeholder="If primary fails, what's the minimum we leave with?"
                className="h-8 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Advancement Goal</Label>
              <Input
                value={form.advancement_goal || ""}
                onChange={(e) => updateField("advancement_goal", e.target.value)}
                placeholder="What pipeline stage movement are we targeting?"
                className="h-8 text-sm"
              />
            </div>
          </div>
        </AccordionSection>

        {/* Section 2: Prospect's Concept */}
        <AccordionSection title="2. Prospect's Concept" icon={Lightbulb}>
          <div className="space-y-2">
            <div>
              <Label className="text-xs">What They Think They Need</Label>
              <Textarea
                value={form.what_they_think_they_need || ""}
                onChange={(e) => updateField("what_they_think_they_need", e.target.value)}
                placeholder="Their stated need or perception of the problem..."
                rows={2}
                className="text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">What They Actually Need</Label>
              <Textarea
                value={form.what_they_actually_need || ""}
                onChange={(e) => updateField("what_they_actually_need", e.target.value)}
                placeholder="The real underlying need we've identified..."
                rows={2}
                className="text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Concept Gap</Label>
              <Input
                value={form.concept_gap || ""}
                onChange={(e) => updateField("concept_gap", e.target.value)}
                placeholder="The gap between perceived and actual need"
                className="h-8 text-sm"
              />
            </div>
          </div>
        </AccordionSection>

        {/* Section 3: Questions */}
        <AccordionSection
          title="3. Questions Planned"
          icon={MessageSquare}
          badge={
            <Badge variant="secondary" className="text-xs">
              {((form.confirmation_questions as ConfirmationQuestion[]) || []).length +
                ((form.new_information_questions as NewInfoQuestion[]) || []).length +
                ((form.attitude_questions as AttitudeQuestion[]) || []).length}
            </Badge>
          }
        >
          <div className="space-y-4">
            <div>
              <h5 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Confirmation Questions</h5>
              <DynamicList<ConfirmationQuestion>
                items={(form.confirmation_questions as ConfirmationQuestion[]) || []}
                onUpdate={(items) => updateField("confirmation_questions", items)}
                newItem={() => ({ question: "", confirming: "" })}
                addLabel="Add question"
                renderItem={(item, _i, update) => (
                  <div className="space-y-1">
                    <Input value={item.question} onChange={(e) => update("question", e.target.value)} placeholder="Question..." className="h-7 text-xs" />
                    <Input value={item.confirming} onChange={(e) => update("confirming", e.target.value)} placeholder="Confirming what?" className="h-7 text-xs text-muted-foreground" />
                  </div>
                )}
              />
            </div>
            <Separator />
            <div>
              <h5 className="text-xs font-semibold text-muted-foreground uppercase mb-2">New Information Questions</h5>
              <DynamicList<NewInfoQuestion>
                items={(form.new_information_questions as NewInfoQuestion[]) || []}
                onUpdate={(items) => updateField("new_information_questions", items)}
                newItem={() => ({ question: "", learning: "", discovery_q: "" })}
                addLabel="Add question"
                renderItem={(item, _i, update) => (
                  <div className="space-y-1">
                    <Input value={item.question} onChange={(e) => update("question", e.target.value)} placeholder="Question..." className="h-7 text-xs" />
                    <Input value={item.learning} onChange={(e) => update("learning", e.target.value)} placeholder="What we'll learn..." className="h-7 text-xs text-muted-foreground" />
                  </div>
                )}
              />
            </div>
            <Separator />
            <div>
              <h5 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Attitude Questions</h5>
              <DynamicList<AttitudeQuestion>
                items={(form.attitude_questions as AttitudeQuestion[]) || []}
                onUpdate={(items) => updateField("attitude_questions", items)}
                newItem={() => ({ question: "", gauging: "" })}
                addLabel="Add question"
                renderItem={(item, _i, update) => (
                  <div className="space-y-1">
                    <Input value={item.question} onChange={(e) => update("question", e.target.value)} placeholder="Question..." className="h-7 text-xs" />
                    <Input value={item.gauging} onChange={(e) => update("gauging", e.target.value)} placeholder="Gauging what attitude?" className="h-7 text-xs text-muted-foreground" />
                  </div>
                )}
              />
            </div>
          </div>
        </AccordionSection>

        {/* Section 4: Information to Share */}
        <AccordionSection
          title="4. Information to Share"
          icon={FileText}
          badge={
            <Badge variant="secondary" className="text-xs">
              {((form.information_to_share as InfoToShare[]) || []).length}
            </Badge>
          }
        >
          <DynamicList<InfoToShare>
            items={(form.information_to_share as InfoToShare[]) || []}
            onUpdate={(items) => updateField("information_to_share", items)}
            newItem={() => ({ info: "", connects_to_concept: "", when: "early" as const })}
            addLabel="Add info"
            renderItem={(item, _i, update) => (
              <div className="space-y-1">
                <Input value={item.info} onChange={(e) => update("info", e.target.value)} placeholder="Information to share..." className="h-7 text-xs" />
                <div className="grid grid-cols-2 gap-2">
                  <Input value={item.connects_to_concept} onChange={(e) => update("connects_to_concept", e.target.value)} placeholder="Connects to concept..." className="h-7 text-xs text-muted-foreground" />
                  <Select value={item.when} onValueChange={(v) => update("when", v)}>
                    <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="early">Early</SelectItem>
                      <SelectItem value="after_probing">After Probing</SelectItem>
                      <SelectItem value="if_objection">If Objection</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          />
        </AccordionSection>

        {/* Section 5: Commitment */}
        <AccordionSection title="5. Commitment to Request" icon={HandshakeIcon}>
          <Textarea
            value={form.close_language || ""}
            onChange={(e) => updateField("close_language", e.target.value)}
            placeholder="What specific commitment will you ask for? Script the exact words..."
            rows={3}
            className="text-sm"
          />
        </AccordionSection>

        {/* Section 6: Credibility */}
        <AccordionSection
          title="6. Credibility Elements"
          icon={Shield}
          badge={
            <Badge variant="secondary" className="text-xs">
              {((form.credibility_elements as CredibilityElement[]) || []).length}
            </Badge>
          }
        >
          <DynamicList<CredibilityElement>
            items={(form.credibility_elements as CredibilityElement[]) || []}
            onUpdate={(items) => updateField("credibility_elements", items)}
            newItem={() => ({ type: "competence" as const, element: "", for_whom: "" })}
            addLabel="Add element"
            renderItem={(item, _i, update) => (
              <div className="grid grid-cols-3 gap-2">
                <Select value={item.type} onValueChange={(v) => update("type", v)}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="competence">Competence</SelectItem>
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="track_record">Track Record</SelectItem>
                    <SelectItem value="relationship">Relationship</SelectItem>
                  </SelectContent>
                </Select>
                <Input value={item.element} onChange={(e) => update("element", e.target.value)} placeholder="Credibility element..." className="h-7 text-xs" />
                <Input value={item.for_whom} onChange={(e) => update("for_whom", e.target.value)} placeholder="For whom?" className="h-7 text-xs" />
              </div>
            )}
          />
        </AccordionSection>

        {/* Section 7: Objections */}
        <AccordionSection
          title="7. Anticipated Objections"
          icon={AlertTriangle}
          badge={
            <Badge variant="secondary" className="text-xs">
              {((form.anticipated_objections as ObjectionItem[]) || []).length}
            </Badge>
          }
        >
          <DynamicList<ObjectionItem>
            items={(form.anticipated_objections as ObjectionItem[]) || []}
            onUpdate={(items) => updateField("anticipated_objections", items)}
            newItem={() => ({ objection: "", response_points: "" })}
            addLabel="Add objection"
            renderItem={(item, _i, update) => (
              <div className="space-y-1">
                <Input value={item.objection} onChange={(e) => update("objection", e.target.value)} placeholder="Anticipated objection..." className="h-7 text-xs" />
                <Textarea value={item.response_points} onChange={(e) => update("response_points", e.target.value)} placeholder="Response points..." rows={2} className="text-xs" />
              </div>
            )}
          />
        </AccordionSection>

        {/* Section 8: Debrief */}
        <AccordionSection
          title="8. Post-Meeting Debrief"
          icon={ClipboardCheck}
          defaultOpen={form.status === "completed" || form.status === "debriefed"}
        >
          {form.status === "debriefed" ? (
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  {form.objective_achieved ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-red-500" />}
                  Objective: {form.objective_achieved ? "Achieved" : "Not met"}
                </span>
                <span className="flex items-center gap-1">
                  {form.fallback_achieved ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-muted-foreground" />}
                  Fallback: {form.fallback_achieved ? "Achieved" : "Not met"}
                </span>
              </div>
              {form.buying_influence_learnings && <p><strong>Learnings:</strong> {form.buying_influence_learnings}</p>}
              {form.new_red_flags && <p className="text-red-600"><strong>Red Flags:</strong> {form.new_red_flags}</p>}
              {form.new_strengths && <p className="text-green-600"><strong>Strengths:</strong> {form.new_strengths}</p>}
              {form.next_action && <p><strong>Next Action:</strong> {form.next_action} {form.next_action_owner && `(${form.next_action_owner})`}</p>}
              {form.debrief_notes && <p className="bg-muted/50 p-2 rounded text-xs whitespace-pre-wrap">{form.debrief_notes}</p>}
            </div>
          ) : (
            <div>
              {!showDebrief ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDebrief(true)}
                  disabled={form.status === "planning"}
                  className="text-xs"
                >
                  <ClipboardCheck className="h-3.5 w-3.5 mr-1" />
                  Start Debrief
                </Button>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={debriefForm.objective_achieved}
                        onCheckedChange={(v) => setDebriefForm((p) => ({ ...p, objective_achieved: v }))}
                      />
                      <Label className="text-xs">Objective Achieved</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={debriefForm.fallback_achieved}
                        onCheckedChange={(v) => setDebriefForm((p) => ({ ...p, fallback_achieved: v }))}
                      />
                      <Label className="text-xs">Fallback Achieved</Label>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Buying Influence Learnings</Label>
                    <Textarea
                      value={debriefForm.buying_influence_learnings}
                      onChange={(e) => setDebriefForm((p) => ({ ...p, buying_influence_learnings: e.target.value }))}
                      placeholder="What did we learn about each buying influence?"
                      rows={2}
                      className="text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-red-600">New Red Flags</Label>
                      <Textarea
                        value={debriefForm.new_red_flags}
                        onChange={(e) => setDebriefForm((p) => ({ ...p, new_red_flags: e.target.value }))}
                        rows={2}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-green-600">New Strengths</Label>
                      <Textarea
                        value={debriefForm.new_strengths}
                        onChange={(e) => setDebriefForm((p) => ({ ...p, new_strengths: e.target.value }))}
                        rows={2}
                        className="text-sm"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Next Action</Label>
                      <Input
                        value={debriefForm.next_action}
                        onChange={(e) => setDebriefForm((p) => ({ ...p, next_action: e.target.value }))}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Action Owner</Label>
                      <Input
                        value={debriefForm.next_action_owner}
                        onChange={(e) => setDebriefForm((p) => ({ ...p, next_action_owner: e.target.value }))}
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Debrief Notes</Label>
                    <Textarea
                      value={debriefForm.debrief_notes}
                      onChange={(e) => setDebriefForm((p) => ({ ...p, debrief_notes: e.target.value }))}
                      rows={3}
                      className="text-sm"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleDebrief} disabled={debriefPrep.isPending} className="text-xs">
                      {debriefPrep.isPending ? "Saving..." : "Save Debrief"}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setShowDebrief(false)} className="text-xs">Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </AccordionSection>
      </div>
    </div>
  );
}

// ============= LIST VIEW =============

function MeetingPrepList({
  preps,
  isLoading,
  onSelect,
  onCreate,
}: {
  preps: MeetingPrepSummary[];
  isLoading: boolean;
  onSelect: (id: string) => void;
  onCreate: () => void;
}) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[200px]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!preps.length) {
    return (
      <div className="text-center py-12">
        <FileText className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
        <h3 className="text-base font-medium mb-1">No Meeting Preps</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Create a Blue Sheet to prepare for your next meeting
        </p>
        <Button variant="outline" size="sm" onClick={onCreate}>
          <Plus className="h-4 w-4 mr-1" />
          New Meeting Prep
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-muted-foreground">
          {preps.length} meeting prep{preps.length === 1 ? "" : "s"}
        </p>
        <Button variant="outline" size="sm" onClick={onCreate} className="text-xs">
          <Plus className="h-3.5 w-3.5 mr-1" />
          New Prep
        </Button>
      </div>
      <div className="space-y-2">
        {preps.map((p) => (
          <Card
            key={p.id}
            className="cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => onSelect(p.id)}
          >
            <CardContent className="p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <MeetingTypeBadge type={p.meeting_type} />
                    <StatusBadge status={p.status} />
                    {p.objective_achieved !== null && (
                      p.objective_achieved
                        ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                        : <XCircle className="h-3.5 w-3.5 text-red-500" />
                    )}
                  </div>
                  {p.primary_objective && (
                    <p className="text-sm line-clamp-1">{p.primary_objective}</p>
                  )}
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <CalendarDays className="h-3 w-3" />
                      {format(new Date(p.meeting_date), "MMM d, yyyy")}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {p.their_attendee_count} attendee{p.their_attendee_count === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ============= PRE-FLIGHT WIZARD =============

function MeetingPrepPreFlight({
  opportunityId,
  opportunityContacts,
  stageName,
  stageDescription,
  onContinue,
  onCancel,
}: {
  opportunityId: string;
  opportunityContacts: OpportunityContact[];
  stageName?: string | null;
  stageDescription?: string | null;
  onContinue: (data: PreFlightData) => void;
  onCancel: () => void;
}) {
  const updateContact = useUpdateOpportunityContact();

  // Contact classification state — init from existing contacts
  const [contactState, setContactState] = useState<
    Array<{
      contact: OpportunityContact;
      buying_role: string;
      relationship_strength: string;
      attending: boolean;
      dirty: boolean;
    }>
  >(() =>
    opportunityContacts.map((c) => ({
      contact: c,
      buying_role: c.buying_role || "unknown",
      relationship_strength: c.relationship_strength || "",
      attending: true,
      dirty: false,
    }))
  );

  // Goal state
  const [selectedGoal, setSelectedGoal] = useState("");
  const [customGoal, setCustomGoal] = useState("");
  const [advancementGoal, setAdvancementGoal] = useState("");

  // Stage context
  const currentStage = PIPELINE_STAGES.find((s) => s.id === stageName);
  const currentStageIdx = PIPELINE_STAGES.findIndex((s) => s.id === stageName);
  const nextStage = currentStageIdx >= 0 && currentStageIdx < PIPELINE_STAGES.length - 2
    ? PIPELINE_STAGES[currentStageIdx + 1]
    : null;
  const stageGoals = stageName ? STAGE_SUGGESTED_GOALS[stageName] : null;

  // Pre-fill advancement goal from stage hint
  useEffect(() => {
    if (stageGoals?.nextStepHint && !advancementGoal) {
      setAdvancementGoal(stageGoals.nextStepHint);
    }
  }, [stageGoals]);

  const unclassifiedCount = contactState.filter(
    (c) => !c.buying_role || c.buying_role === "unknown"
  ).length;

  const [savingContacts, setSavingContacts] = useState(false);

  const handleSaveClassifications = async () => {
    const dirtyContacts = contactState.filter((c) => c.dirty);
    if (!dirtyContacts.length) return;

    setSavingContacts(true);
    try {
      for (const c of dirtyContacts) {
        await updateContact.mutateAsync({
          id: c.contact.id,
          opportunity_id: opportunityId,
          buying_role: c.buying_role as OpportunityContact["buying_role"],
          relationship_strength: c.relationship_strength as OpportunityContact["relationship_strength"],
        });
      }
      setContactState((prev) =>
        prev.map((c) => ({ ...c, dirty: false }))
      );
      toast.success(`Updated ${dirtyContacts.length} contact${dirtyContacts.length > 1 ? "s" : ""}`);
    } catch {
      toast.error("Failed to save contact classifications");
    } finally {
      setSavingContacts(false);
    }
  };

  const handleContinue = async () => {
    // Auto-save dirty contacts before continuing
    const dirtyContacts = contactState.filter((c) => c.dirty);
    if (dirtyContacts.length) {
      setSavingContacts(true);
      try {
        for (const c of dirtyContacts) {
          await updateContact.mutateAsync({
            id: c.contact.id,
            opportunity_id: opportunityId,
            buying_role: c.buying_role as OpportunityContact["buying_role"],
            relationship_strength: c.relationship_strength as OpportunityContact["relationship_strength"],
          });
        }
      } catch {
        toast.error("Failed to save contact classifications");
        setSavingContacts(false);
        return;
      }
      setSavingContacts(false);
    }

    const goal = selectedGoal === "__custom__" ? customGoal : selectedGoal;
    const attendees: AttendeeItem[] = contactState
      .filter((c) => c.attending)
      .map((c) => ({
        contact_id: c.contact.contact_id || undefined,
        name: c.contact.contact_name,
        buying_role: c.buying_role || "",
        key_concern: "",
      }));

    onContinue({
      attendees,
      primaryObjective: goal,
      advancementGoal,
    });
  };

  const hasDirty = contactState.some((c) => c.dirty);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-2 pb-2 border-b">
        <Button variant="ghost" size="sm" onClick={onCancel} className="h-8 w-8 p-0">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h3 className="text-sm font-semibold">Pre-Flight Checklist</h3>
          <p className="text-xs text-muted-foreground">Review contacts & set meeting goal before creating Blue Sheet</p>
        </div>
      </div>

      {/* Section A: Contact Classification */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" />
            <h4 className="text-sm font-medium">Contact Classification</h4>
            {unclassifiedCount > 0 && (
              <Badge variant="destructive" className="text-xs">
                {unclassifiedCount} unclassified
              </Badge>
            )}
          </div>
          {hasDirty && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleSaveClassifications}
              disabled={savingContacts}
              className="text-xs"
            >
              {savingContacts ? "Saving..." : "Save Classifications"}
            </Button>
          )}
        </div>

        {contactState.length === 0 ? (
          <div className="text-center py-6 border rounded-lg bg-muted/30">
            <Users className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              No contacts on this opportunity yet. Add contacts in the Contacts tab first.
            </p>
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            {/* Table header */}
            <div className="grid grid-cols-[auto_1fr_1fr_1fr_auto] gap-2 px-3 py-2 bg-muted/50 text-xs font-medium text-muted-foreground">
              <span className="w-5"></span>
              <span>Contact</span>
              <span>Buying Role</span>
              <span>Relationship</span>
              <span className="w-16 text-center">Attending</span>
            </div>

            {/* Contact rows */}
            {contactState.map((item, idx) => {
              const roleConfig = BUYING_ROLE_CONFIG.find((r) => r.value === item.buying_role);
              const isUnclassified = !item.buying_role || item.buying_role === "unknown";

              return (
                <div
                  key={item.contact.id}
                  className={`grid grid-cols-[auto_1fr_1fr_1fr_auto] gap-2 px-3 py-2 items-center border-t ${
                    isUnclassified ? "bg-amber-50/50" : ""
                  }`}
                >
                  {/* Role icon */}
                  <div className="w-5">
                    {roleConfig && !isUnclassified ? (
                      <roleConfig.icon className={`h-4 w-4 ${roleConfig.color}`} />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                    )}
                  </div>

                  {/* Name + title */}
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{item.contact.contact_name}</p>
                    {item.contact.contact_title && (
                      <p className="text-xs text-muted-foreground truncate">{item.contact.contact_title}</p>
                    )}
                  </div>

                  {/* Buying role dropdown */}
                  <Select
                    value={item.buying_role}
                    onValueChange={(v) => {
                      setContactState((prev) => {
                        const next = [...prev];
                        next[idx] = { ...next[idx], buying_role: v, dirty: true };
                        return next;
                      });
                    }}
                  >
                    <SelectTrigger className={`h-7 text-xs ${isUnclassified ? "border-amber-300" : ""}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BUYING_ROLE_CONFIG.map((r) => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Relationship strength dropdown */}
                  <Select
                    value={item.relationship_strength || "none"}
                    onValueChange={(v) => {
                      setContactState((prev) => {
                        const next = [...prev];
                        next[idx] = { ...next[idx], relationship_strength: v === "none" ? "" : v, dirty: true };
                        return next;
                      });
                    }}
                  >
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue placeholder="Not set" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not set</SelectItem>
                      {RELATIONSHIP_STRENGTH_OPTIONS.map((r) => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Attending checkbox */}
                  <div className="w-16 flex justify-center">
                    <button
                      type="button"
                      className={`h-5 w-5 rounded border flex items-center justify-center transition-colors ${
                        item.attending
                          ? "bg-primary border-primary text-primary-foreground"
                          : "border-input hover:border-primary/50"
                      }`}
                      onClick={() => {
                        setContactState((prev) => {
                          const next = [...prev];
                          next[idx] = { ...next[idx], attending: !next[idx].attending };
                          return next;
                        });
                      }}
                    >
                      {item.attending && <CheckSquare className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Section B: Stage Context & Meeting Goal */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-primary" />
          <h4 className="text-sm font-medium">Meeting Goal</h4>
        </div>

        {/* Stage context card */}
        {currentStage && (
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Badge
                  style={{ backgroundColor: currentStage.color, color: "#fff" }}
                  className="text-xs"
                >
                  {currentStage.id}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  ({currentStage.probability}% probability)
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {stageDescription || currentStage.description}
              </p>
              {nextStage && (
                <div className="flex items-center gap-1.5 pt-1">
                  <ArrowRight className="h-3 w-3 text-primary" />
                  <span className="text-xs font-medium">
                    Next stage: {nextStage.id}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    — {nextStage.description}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Suggested goals */}
        {stageGoals && (
          <div className="space-y-2">
            <Label className="text-xs font-medium">Suggested Goals for This Stage</Label>
            <div className="space-y-1.5">
              {stageGoals.goals.map((goal) => (
                <button
                  key={goal}
                  type="button"
                  className={`w-full text-left px-3 py-2 rounded-lg border text-xs transition-colors ${
                    selectedGoal === goal
                      ? "border-primary bg-primary/10 text-primary font-medium"
                      : "border-input hover:border-primary/40 hover:bg-muted/50"
                  }`}
                  onClick={() => setSelectedGoal(goal)}
                >
                  {goal}
                </button>
              ))}
              <button
                type="button"
                className={`w-full text-left px-3 py-2 rounded-lg border text-xs transition-colors ${
                  selectedGoal === "__custom__"
                    ? "border-primary bg-primary/10 text-primary font-medium"
                    : "border-dashed border-input hover:border-primary/40"
                }`}
                onClick={() => setSelectedGoal("__custom__")}
              >
                + Custom goal...
              </button>
            </div>
          </div>
        )}

        {/* Custom goal input */}
        {(selectedGoal === "__custom__" || !stageGoals) && (
          <div>
            <Label className="text-xs">Meeting Goal</Label>
            <Textarea
              value={customGoal}
              onChange={(e) => setCustomGoal(e.target.value)}
              placeholder="What specific outcome must we achieve in this meeting?"
              rows={2}
              className="text-sm"
            />
          </div>
        )}

        {/* Advancement goal */}
        <div>
          <Label className="text-xs">Advancement Goal (Next Step)</Label>
          <Textarea
            value={advancementGoal}
            onChange={(e) => setAdvancementGoal(e.target.value)}
            placeholder="What pipeline stage movement are we targeting?"
            rows={2}
            className="text-sm"
          />
          {stageGoals?.nextStepHint && advancementGoal !== stageGoals.nextStepHint && (
            <button
              type="button"
              className="text-xs text-primary hover:underline mt-1"
              onClick={() => setAdvancementGoal(stageGoals.nextStepHint)}
            >
              <Info className="h-3 w-3 inline mr-1" />
              Use suggested: "{stageGoals.nextStepHint.slice(0, 60)}..."
            </button>
          )}
        </div>
      </div>

      {/* Continue button */}
      <div className="flex gap-2 pt-2 border-t">
        <Button
          onClick={handleContinue}
          disabled={savingContacts || (!selectedGoal && !customGoal)}
          className="flex-1"
        >
          {savingContacts ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
              Saving contacts...
            </>
          ) : (
            <>
              Continue to Blue Sheet
              <ArrowRight className="h-4 w-4 ml-1" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// ============= CREATE FORM =============

function CreateMeetingPrepForm({
  opportunityId,
  accountId,
  opportunityContacts,
  preFlightData,
  onCreated,
  onCancel,
}: {
  opportunityId: string;
  accountId: string;
  opportunityContacts?: OpportunityContact[];
  preFlightData?: PreFlightData | null;
  onCreated: (id: string) => void;
  onCancel: () => void;
}) {
  const createPrep = useCreateMeetingPrep();
  const [meetingDate, setMeetingDate] = useState("");
  const [meetingType, setMeetingType] = useState<MeetingType>("discovery");
  const [primaryObjective, setPrimaryObjective] = useState(preFlightData?.primaryObjective || "");
  const [advancementGoal, setAdvancementGoal] = useState(preFlightData?.advancementGoal || "");
  const [ourAttendees, setOurAttendees] = useState("");
  const [theirAttendees, setTheirAttendees] = useState<AttendeeItem[]>(
    preFlightData?.attendees || []
  );

  // Pre-fill their_attendees from opportunity contacts (only if no preFlightData)
  useEffect(() => {
    if (!preFlightData && opportunityContacts?.length) {
      setTheirAttendees(
        opportunityContacts.map((c) => ({
          contact_id: c.contact_id || undefined,
          name: c.contact_name,
          buying_role: c.buying_role || "",
          key_concern: "",
        }))
      );
    }
  }, [opportunityContacts, preFlightData]);

  // Default meeting date to next business day
  useEffect(() => {
    const now = new Date();
    const next = new Date(now);
    next.setDate(next.getDate() + 1);
    while (next.getDay() === 0 || next.getDay() === 6) {
      next.setDate(next.getDate() + 1);
    }
    next.setHours(10, 0, 0, 0);
    setMeetingDate(next.toISOString().slice(0, 16));
  }, []);

  const handleCreate = () => {
    if (!meetingDate) {
      toast.error("Please set a meeting date");
      return;
    }
    createPrep.mutate(
      {
        opportunity_id: opportunityId,
        account_id: accountId,
        meeting_date: new Date(meetingDate).toISOString(),
        meeting_type: meetingType,
        primary_objective: primaryObjective,
        advancement_goal: advancementGoal || null,
        our_attendees: ourAttendees.split(",").map((s: string) => s.trim()).filter(Boolean),
        their_attendees: theirAttendees.filter((a) => a.name.trim()),
      },
      {
        onSuccess: (id) => {
          toast.success("Meeting prep created");
          onCreated(id);
        },
        onError: () => toast.error("Failed to create"),
      }
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 pb-2 border-b">
        <Button variant="ghost" size="sm" onClick={onCancel} className="h-8 w-8 p-0">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h3 className="text-sm font-semibold">New Meeting Prep</h3>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Meeting Date & Time *</Label>
          <Input
            type="datetime-local"
            value={meetingDate}
            onChange={(e) => setMeetingDate(e.target.value)}
            className="h-8 text-sm"
          />
        </div>
        <div>
          <Label className="text-xs">Meeting Type</Label>
          <Select value={meetingType} onValueChange={(v) => setMeetingType(v as MeetingType)}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MEETING_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label className="text-xs">Our Attendees (comma-separated)</Label>
        <Input
          value={ourAttendees}
          onChange={(e) => setOurAttendees(e.target.value)}
          placeholder="e.g., Brian Elber, Nico Carr"
          className="h-8 text-sm"
        />
      </div>

      <div>
        <Label className="text-xs">Their Attendees {theirAttendees.length > 0 && `(${theirAttendees.length} pre-filled from contacts)`}</Label>
        <DynamicList<AttendeeItem>
          items={theirAttendees}
          onUpdate={setTheirAttendees}
          newItem={() => ({ name: "", buying_role: "", key_concern: "" })}
          addLabel="Add attendee"
          renderItem={(item, _i, update) => (
            <div className="grid grid-cols-3 gap-2">
              <Input value={item.name} onChange={(e) => update("name", e.target.value)} placeholder="Name" className="h-7 text-xs" />
              <Select value={item.buying_role || ""} onValueChange={(v) => update("buying_role", v)}>
                <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Buying role" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="economic">Economic Buyer</SelectItem>
                  <SelectItem value="user">User Buyer</SelectItem>
                  <SelectItem value="technical">Technical Buyer</SelectItem>
                  <SelectItem value="coach">Coach/Champion</SelectItem>
                  <SelectItem value="unknown">Unknown</SelectItem>
                </SelectContent>
              </Select>
              <Input value={item.key_concern || ""} onChange={(e) => update("key_concern", e.target.value)} placeholder="Key concern" className="h-7 text-xs" />
            </div>
          )}
        />
      </div>

      <div>
        <Label className="text-xs">Primary Objective {preFlightData && "(from Pre-Flight)"}</Label>
        <Textarea
          value={primaryObjective}
          onChange={(e) => setPrimaryObjective(e.target.value)}
          placeholder="What specific outcome must we achieve in this meeting?"
          rows={2}
          className="text-sm"
        />
      </div>

      <div>
        <Label className="text-xs">Advancement Goal {preFlightData && "(from Pre-Flight)"}</Label>
        <Input
          value={advancementGoal}
          onChange={(e) => setAdvancementGoal(e.target.value)}
          placeholder="What pipeline stage movement are we targeting?"
          className="h-8 text-sm"
        />
      </div>

      <Button onClick={handleCreate} disabled={createPrep.isPending} className="w-full">
        {createPrep.isPending ? "Creating..." : "Create Meeting Prep"}
      </Button>
    </div>
  );
}

// ============= MAIN EXPORT =============

export function MeetingPrepTab({
  opportunityId,
  accountId,
  accountName,
  opportunityName,
  opportunityContacts,
  stageName,
  stageDescription,
}: MeetingPrepTabProps) {
  const { data: preps = [], isLoading } = useMeetingPreps(opportunityId);
  const [view, setView] = useState<"list" | "preflight" | "create" | "detail">("list");
  const [selectedPrepId, setSelectedPrepId] = useState<string | null>(null);
  const [preFlightData, setPreFlightData] = useState<PreFlightData | null>(null);

  if (!accountId) {
    return (
      <div className="text-center py-12 text-sm text-muted-foreground">
        Link an account to this opportunity to use Meeting Prep.
      </div>
    );
  }

  if (view === "preflight") {
    return (
      <MeetingPrepPreFlight
        opportunityId={opportunityId}
        opportunityContacts={opportunityContacts || []}
        stageName={stageName}
        stageDescription={stageDescription}
        onContinue={(data) => {
          setPreFlightData(data);
          setView("create");
        }}
        onCancel={() => { setView("list"); setPreFlightData(null); }}
      />
    );
  }

  if (view === "create") {
    return (
      <CreateMeetingPrepForm
        opportunityId={opportunityId}
        accountId={accountId}
        opportunityContacts={opportunityContacts}
        preFlightData={preFlightData}
        onCreated={(id) => {
          setSelectedPrepId(id);
          setPreFlightData(null);
          setView("detail");
        }}
        onCancel={() => { setView("list"); setPreFlightData(null); }}
      />
    );
  }

  if (view === "detail" && selectedPrepId) {
    return (
      <MeetingPrepDetail
        prepId={selectedPrepId}
        accountName={accountName || "Account"}
        opportunityName={opportunityName}
        onBack={() => { setView("list"); setSelectedPrepId(null); }}
        onDeleted={() => { setView("list"); setSelectedPrepId(null); }}
      />
    );
  }

  return (
    <MeetingPrepList
      preps={preps}
      isLoading={isLoading}
      onSelect={(id) => { setSelectedPrepId(id); setView("detail"); }}
      onCreate={() => setView("preflight")}
    />
  );
}
